import { useCallback } from "react";
import { uuidv4 } from "@capacit-com/lara-law-shared-ui/uuid";
import {
  UploadItem,
  UploadItemStatus,
  UploadItemType,
  UploadSource,
} from "@/components/upload/types/upload-item";
import {
  FolderMetadataMap,
  FolderMetadataRecord,
} from "@/types/folder-metadata";
import { WorkspaceDocumentMetadata } from "@/models/metadata/metadata";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { getAllWorkspaceItems } from "../utils/workspaceItemUtils";
import { findFolderIdByPath } from "../utils/folderUtils";
import Workspace from "@/models/workspace/Workspace";
import useAzureStorage from "./useAzureStorage";
import useWorkspace from "@/hooks/useWorkspace";
import { useMicrosoftDocxPdfStaging } from "./useMicrosoftDocxPdfStaging";
import { logger } from "@/lib/logger";
import {
  checkForFolderConflicts,
  checkForFileNameConflictsInFolders,
  getWorkspaceFileNames,
} from "../services/conflictDetectionService";
import {
  FileWithRelativePath,
  SharePointFileMetadata,
} from "../types/upload.types";

type UseFolderUploadParams = {
  workspaceId: string | null;
  currentWorkspaceIdRef: React.MutableRefObject<string | null>;
  updateUploadsForWorkspace: (
    targetWorkspaceId: string,
    updater: (prev: UploadItem[]) => UploadItem[]
  ) => void;
  // Dialog state setters
  setConflictingFiles: (files: File[]) => void;
  setPendingUploadFiles: (files: FileList | null) => void;
  setPendingWorkspaceFileNames: (names: Record<string, string>) => void;
  setPendingFolderId: (id: string | undefined) => void;
  setPendingFolderMetadataMap: (
    map:
      | [
          string,
          {
            source: UploadSource;
            driveId?: string;
            siteId?: string;
            sharepointFileId?: string;
          },
        ][]
      | null
  ) => void;
  setPendingFileMetadataMap: (
    map:
      | [
          string,
          {
            source: UploadSource;
            driveId?: string;
            siteId?: string;
            sharepointFileId?: string;
          },
        ][]
      | null
  ) => void;
  setIsConflictDialogOpen: (open: boolean) => void;
  setPendingMetadataFiles: (files: FileList | null) => void;
  setPendingMetadataWorkspaceFileNames: (names: Record<string, string>) => void;
  setPendingMetadataFolderId: (id: string | undefined) => void;
  setIsUploadMetadataDialogOpen: (open: boolean) => void;
  // Tenant metadata check
  ensureTenantMetadata: () => Promise<{ id?: string }[]>;
};

type UseFolderUploadReturn = {
  handleFolderUpload: (
    files: FileList,
    workspace: Workspace,
    folderId?: string,
    providedMetadata?: Record<string, WorkspaceDocumentMetadata[]>,
    skipConflictCheck?: boolean,
    skipMetadataCheck?: boolean,
    fileMetadataMap?: Map<string, SharePointFileMetadata>,
    folderMetadataMap?: FolderMetadataMap
  ) => Promise<void>;
};

/**
 * Extract folder paths from files with webkitRelativePath
 */
const extractAllFolderPaths = (
  filesArray: File[],
  folderMetadataMap?: FolderMetadataMap
): { folderPathsSet: Set<string>; rootFolderPath: string | null } => {
  const folderPathsSet = new Set<string>();
  let rootFolderPath: string | null = null;

  filesArray.forEach((file) => {
    const relativePath =
      (file as FileWithRelativePath).webkitRelativePath || file.name;
    const pathParts = relativePath.split("/").filter(Boolean);

    // Store the root folder path (first part) if it exists
    if (pathParts.length > 1 && rootFolderPath === null) {
      const firstPart = pathParts[0];
      if (firstPart && firstPart.trim().length > 0) {
        rootFolderPath = firstPart;
      }
    }

    // Extract all folder paths from the relative path
    for (let i = 1; i < pathParts.length; i++) {
      const folderPath = pathParts.slice(0, i).join("/");
      if (folderPath && folderPath.trim().length > 0) {
        folderPathsSet.add(folderPath);
      }
    }
  });

  // Add empty folders from SharePoint metadata
  if (folderMetadataMap && folderMetadataMap.size > 0) {
    folderMetadataMap.forEach((_, folderPath) => {
      if (folderPath && folderPath.trim().length > 0) {
        folderPathsSet.add(folderPath);
        // Also add parent paths if they don't exist
        const pathParts = folderPath.split("/").filter(Boolean);
        for (let i = 1; i < pathParts.length; i++) {
          const parentPath = pathParts.slice(0, i).join("/");
          if (parentPath && parentPath.trim().length > 0) {
            folderPathsSet.add(parentPath);
          }
        }
      }
    });
  }

  // Ensure root folder is always included if we have nested files
  if (rootFolderPath) {
    const trimmedPath = String(rootFolderPath).trim();
    if (trimmedPath.length > 0 && !folderPathsSet.has(rootFolderPath)) {
      folderPathsSet.add(rootFolderPath);
    }
  }

  return { folderPathsSet, rootFolderPath };
};

/**
 * Create upload items for folders
 */
const createFolderUploadItems = (
  allFolderPaths: string[],
  existingPaths: Set<string>
): UploadItem[] => {
  const items = allFolderPaths
    .filter((folderPath) => folderPath && folderPath.trim().length > 0)
    .sort((a, b) => {
      const depthA = a.split("/").length;
      const depthB = b.split("/").length;
      if (depthA !== depthB) {
        return depthA - depthB;
      }
      return a.localeCompare(b);
    })
    .map((folderPath) => ({
      id: uuidv4(),
      name: folderPath,
      type: UploadItemType.FOLDER,
      status: existingPaths.has(folderPath)
        ? UploadItemStatus.Completed
        : UploadItemStatus.Completed, // Folders are created synchronously
      source: UploadSource.LOCAL,
    }));

  return items;
};

/**
 * Create upload items for files
 */
const createFileUploadItems = (filesArray: File[]): UploadItem[] => {
  const items = filesArray.map((file) => {
    const uniqueId = uuidv4();
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const type: UploadItemType =
      {
        pdf: UploadItemType.PDF,
        doc: UploadItemType.DOC,
        docx: UploadItemType.DOCX,
        xls: UploadItemType.XLS,
        xlsx: UploadItemType.XLSX,
        xlsm: UploadItemType.XLSM,
      }[fileExtension!] ?? UploadItemType.UNKNOWN;

    const relativePath =
      (file as FileWithRelativePath).webkitRelativePath || file.name;

    return {
      id: uniqueId,
      name: relativePath,
      type,
      status: UploadItemStatus.Pending,
      source: UploadSource.LOCAL,
    };
  });

  return items;
};

/**
 * Hook to handle folder upload operations
 */
export const useFolderUpload = ({
  workspaceId,
  currentWorkspaceIdRef,
  updateUploadsForWorkspace,
  setConflictingFiles,
  setPendingUploadFiles,
  setPendingWorkspaceFileNames,
  setPendingFolderId,
  setPendingFolderMetadataMap,
  setPendingFileMetadataMap,
  setIsConflictDialogOpen,
  setPendingMetadataFiles,
  setPendingMetadataWorkspaceFileNames,
  setPendingMetadataFolderId,
  setIsUploadMetadataDialogOpen,
  ensureTenantMetadata,
}: UseFolderUploadParams): UseFolderUploadReturn => {
  const { uploadFileToAzure } = useAzureStorage();
  const { addDocuments, addFolder } = useWorkspace();
  const { stageMicrosoftPdfIfNeeded } = useMicrosoftDocxPdfStaging();

  const handleFolderUpload = useCallback(
    async (
      files: FileList,
      workspace: Workspace,
      folderId?: string,
      providedMetadata?: Record<string, WorkspaceDocumentMetadata[]>,
      skipConflictCheck?: boolean,
      skipMetadataCheck?: boolean,
      fileMetadataMap?: Map<string, SharePointFileMetadata>,
      folderMetadataMap?: FolderMetadataMap
    ) => {
      const filesArray = Array.from(files) as File[];
      const currentWorkspaceId = workspaceId || currentWorkspaceIdRef.current;

      if (!currentWorkspaceId) {
        logger.error("Upload aborted: no workspace id", {
          operation: "uploadFolder",
          folderId,
        });
        showErrorToast("Workspace ID mangler. Prøv at opdatere siden.");
        return;
      }

      // Allow empty folder creation even if there are no files
      const hasEmptyFolders = folderMetadataMap && folderMetadataMap.size > 0;
      if (filesArray.length === 0 && !hasEmptyFolders) {
        return;
      }

      // Step 1: Check for conflicts if not skipped
      if (!skipConflictCheck) {
        const folderPathsSet = new Set<string>();
        filesArray.forEach((file) => {
          const relativePath =
            (file as FileWithRelativePath).webkitRelativePath || file.name;
          const pathParts = relativePath.split("/");
          for (let i = 1; i < pathParts.length; i++) {
            const folderPath = pathParts.slice(0, i).join("/");
            folderPathsSet.add(folderPath);
          }
        });
        const folderPaths = Array.from(folderPathsSet);

        const { existingPaths } = checkForFolderConflicts(
          folderPaths,
          workspace,
          folderId
        );

        const fileConflicts = checkForFileNameConflictsInFolders(
          filesArray,
          workspace,
          folderId,
          existingPaths
        );

        if (fileConflicts.length > 0) {
          const dataTransfer = new DataTransfer();
          filesArray.forEach((file) => dataTransfer.items.add(file));
          const filteredFiles = dataTransfer.files;

          setConflictingFiles(fileConflicts);
          setPendingUploadFiles(filteredFiles);
          setPendingWorkspaceFileNames(
            getWorkspaceFileNames(
              getAllWorkspaceItems(workspace),
              workspace,
              folderId
            )
          );
          setPendingFolderId(folderId);
          if (folderMetadataMap && folderMetadataMap.size > 0) {
            setPendingFolderMetadataMap(
              Array.from(folderMetadataMap.entries())
            );
          } else {
            setPendingFolderMetadataMap(null);
          }
          if (fileMetadataMap && fileMetadataMap.size > 0) {
            setPendingFileMetadataMap(Array.from(fileMetadataMap.entries()));
          } else {
            setPendingFileMetadataMap(null);
          }
          setIsConflictDialogOpen(true);
          return;
        }
      }

      // Step 2: Check for metadata requirements if not skipped
      if (!skipMetadataCheck) {
        const metadataList = await ensureTenantMetadata();
        if (metadataList && metadataList.length > 0) {
          const dataTransfer = new DataTransfer();
          filesArray.forEach((file) => dataTransfer.items.add(file));
          const filteredFiles = dataTransfer.files;

          setPendingMetadataFiles(filteredFiles);
          setPendingMetadataWorkspaceFileNames(
            getWorkspaceFileNames(
              getAllWorkspaceItems(workspace),
              workspace,
              folderId
            )
          );
          setPendingMetadataFolderId(folderId);
          if (folderMetadataMap && folderMetadataMap.size > 0) {
            setPendingFolderMetadataMap(
              Array.from(folderMetadataMap.entries())
            );
          } else {
            setPendingFolderMetadataMap(null);
          }
          if (fileMetadataMap && fileMetadataMap.size > 0) {
            setPendingFileMetadataMap(Array.from(fileMetadataMap.entries()));
          } else {
            setPendingFileMetadataMap(null);
          }
          setIsUploadMetadataDialogOpen(true);
          return;
        }
      }

      // Step 3: Extract and create folders
      const { folderPathsSet } = extractAllFolderPaths(
        filesArray,
        folderMetadataMap
      );
      const allFolderPaths = Array.from(folderPathsSet).filter(
        (path) => path && path.trim().length > 0
      );

      const { existingPaths, newPaths } = checkForFolderConflicts(
        allFolderPaths,
        workspace,
        folderId
      );

      let updatedWorkspace = workspace;

      // Build pathMetadataMap from folderMetadataMap
      const pathMetadataMap: FolderMetadataRecord | undefined =
        folderMetadataMap && folderMetadataMap.size > 0
          ? Object.fromEntries(
              Array.from(folderMetadataMap.entries())
                .filter(([path]) => allFolderPaths.includes(path))
                .map(([path, metadata]) => [
                  path,
                  {
                    source: metadata.source,
                    driveId: metadata.driveId,
                    siteId: metadata.siteId,
                    sharepointFileId: metadata.sharepointFileId,
                  },
                ])
            )
          : undefined;

      if (newPaths.length > 0) {
        try {
          updatedWorkspace = await addFolder.mutateAsync({
            workspaceId: workspace.id,
            requestBody: {
              paths: newPaths,
              parentFolderId: folderId,
              pathMetadataMap: pathMetadataMap,
            },
          });
        } catch (error) {
          logger.error("Failed to add folders to workspace", {
            operation: "uploadFolder",
            error,
            workspaceId: workspace.id,
            folderId,
            folderCount: newPaths.length,
          });
          showErrorToast("Fejl ved oprettelse af mapper. Prøv igen senere.");
          return;
        }
      } else if (pathMetadataMap && Object.keys(pathMetadataMap).length > 0) {
        try {
          updatedWorkspace = await addFolder.mutateAsync({
            workspaceId: workspace.id,
            requestBody: {
              paths: [],
              parentFolderId: folderId,
              pathMetadataMap: pathMetadataMap,
            },
          });
        } catch (error) {
          logger.error("Failed to update folder metadata", {
            operation: "uploadFolder",
            error,
            workspaceId: workspace.id,
            folderId,
          });
          showErrorToast(
            "Fejl ved opdatering af mappemetadata. Prøv igen senere."
          );
          return;
        }
      }

      // Step 4: Create upload items for UI tracking
      const folderUploadItems = createFolderUploadItems(
        allFolderPaths,
        existingPaths
      );
      const fileUploadItems = createFileUploadItems(filesArray);

      const allUploadItems = [...folderUploadItems, ...fileUploadItems];
      updateUploadsForWorkspace(currentWorkspaceId, (prev) => [
        ...prev,
        ...allUploadItems,
      ]);

      // Step 5: Map files to folder IDs
      const filesToUpload: Array<{
        file: File;
        folderId?: string;
        metadata?: WorkspaceDocumentMetadata[];
        uploadItem: UploadItem;
      }> = [];

      filesArray.forEach((file, index) => {
        const relativePath =
          (file as FileWithRelativePath).webkitRelativePath || file.name;
        const pathParts = relativePath.split("/");
        const folderPath =
          pathParts.length > 1 ? pathParts.slice(0, -1).join("/") : undefined;

        let targetFolderId: string | undefined = undefined;

        if (folderPath) {
          if (existingPaths.has(folderPath)) {
            targetFolderId = findFolderIdByPath(
              workspace,
              folderPath,
              folderId
            );
          } else {
            targetFolderId = findFolderIdByPath(
              updatedWorkspace,
              folderPath,
              folderId
            );
          }
        } else {
          targetFolderId = folderId;
        }

        const metadata = providedMetadata?.[file.name] || [];
        const uploadItem = fileUploadItems[index];

        filesToUpload.push({
          file,
          folderId: targetFolderId,
          metadata,
          uploadItem,
        });
      });

      // Step 6: Upload files to Azure and create documents
      try {
        const originalUploadItemIds = filesToUpload.map(
          (ftu) => ftu.uploadItem.id
        );

        const uploadItemIdToBlobIdMap = new Map<string, string>();
        originalUploadItemIds.forEach((originalId) => {
          const blobId = uuidv4();
          uploadItemIdToBlobIdMap.set(originalId, blobId);
        });

        updateUploadsForWorkspace(currentWorkspaceId, (prev) =>
          prev.map((item) => {
            const blobId = uploadItemIdToBlobIdMap.get(item.id);
            if (blobId && item.type !== UploadItemType.FOLDER) {
              return {
                ...item,
                id: blobId,
              };
            }
            return item;
          })
        );

        await Promise.all(
          filesToUpload.map((ftu, index) => {
            const blobId = uploadItemIdToBlobIdMap.get(
              originalUploadItemIds[index]
            )!;

            return (async () => {
              await uploadFileToAzure(ftu.file, blobId, currentWorkspaceId);

              const fileWithMetadata = ftu.file as FileWithRelativePath;
              let spMetadata = fileWithMetadata._sharePointMetadata;
              if (!spMetadata && fileMetadataMap) {
                const relativePath =
                  fileWithMetadata.webkitRelativePath || ftu.file.name;
                spMetadata = fileMetadataMap.get(relativePath);
                if (!spMetadata) {
                  spMetadata = fileMetadataMap.get(ftu.file.name);
                }
                if (!spMetadata) {
                  for (const [key, value] of fileMetadataMap) {
                    if (key.endsWith(`/${ftu.file.name}`)) {
                      spMetadata = value;
                      break;
                    }
                  }
                }
              }

              await stageMicrosoftPdfIfNeeded(
                ftu.file.name,
                blobId,
                currentWorkspaceId,
                {
                  driveId: spMetadata?.driveId,
                  siteId: spMetadata?.siteId,
                  sharepointFileId: spMetadata?.sharepointFileId,
                  source: spMetadata?.source,
                }
              );
            })();
          })
        );

        const documents = filesToUpload.map((ftu, index) => {
          const { file, folderId: fileFolderId, metadata } = ftu;
          const blobId = uploadItemIdToBlobIdMap.get(
            originalUploadItemIds[index]
          )!;
          const workspaceFileNamesMap = getWorkspaceFileNames(
            getAllWorkspaceItems(updatedWorkspace),
            updatedWorkspace,
            fileFolderId
          );
          const existingDocumentId =
            workspaceFileNamesMap[file.name.toLowerCase()];

          const fileWithMetadata = file as FileWithRelativePath;
          let spMetadata = fileWithMetadata._sharePointMetadata;

          if (!spMetadata && fileMetadataMap) {
            const relativePath =
              fileWithMetadata.webkitRelativePath || file.name;
            spMetadata = fileMetadataMap.get(relativePath);
            if (!spMetadata) {
              spMetadata = fileMetadataMap.get(file.name);
            }
            if (!spMetadata) {
              for (const [key, value] of fileMetadataMap) {
                if (key.endsWith(`/${file.name}`)) {
                  spMetadata = value;
                  break;
                }
              }
            }
          }

          return {
            documentName: file.name,
            documentBlobName: blobId,
            documentSource: spMetadata?.source || UploadSource.LOCAL,
            driveId: spMetadata?.driveId,
            siteId: spMetadata?.siteId,
            sharepointFileId: spMetadata?.sharepointFileId,
            documentId: existingDocumentId || undefined,
            documentMetadataList: metadata,
            folderId: fileFolderId,
          };
        });

        await addDocuments.mutateAsync({
          workspaceId: currentWorkspaceId,
          requestBody: {
            sessionId: currentWorkspaceId,
            documents,
          },
        });
      } catch (error) {
        logger.error("Failed to upload folder files", {
          operation: "uploadFolder",
          error,
          workspaceId: currentWorkspaceId,
          folderId,
          fileCount: filesArray.length,
        });
        showErrorToast("Fejl ved upload af filer. Prøv igen senere.");
      }
    },
    [
      workspaceId,
      currentWorkspaceIdRef,
      ensureTenantMetadata,
      setConflictingFiles,
      setPendingUploadFiles,
      setPendingWorkspaceFileNames,
      setPendingFolderId,
      setIsConflictDialogOpen,
      setIsUploadMetadataDialogOpen,
      setPendingMetadataFiles,
      setPendingMetadataWorkspaceFileNames,
      setPendingMetadataFolderId,
      setPendingFolderMetadataMap,
      setPendingFileMetadataMap,
      addFolder,
      updateUploadsForWorkspace,
      uploadFileToAzure,
      addDocuments,
      stageMicrosoftPdfIfNeeded,
    ]
  );

  return {
    handleFolderUpload,
  };
};
